import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import Layout from '../components/layout/Layout';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane } from 'react-icons/fa';
import axios from 'axios';
import './LegalPages.css';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError('All fields are required');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Send form data to backend
      const apiUrl = process.env.REACT_APP_API_URL || 'https://unitrade-backend-2xrfc6565q-ew.a.run.app/api';
      const response = await axios.post(`${apiUrl}/users/contact/`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }
    } catch (err) {
      console.error('Error submitting contact form:', err);
      const errorMessage = err.response?.data?.error || 
                          'Failed to send message. Please try again or email us directly at support@unitradegh.com';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Container className="legal-page contact-page">
        <Row className="justify-content-center">
          <Col lg={11} xl={10}>
            <h1 className="page-title text-center mb-5">Contact Us</h1>
            
            <Row className="g-4 align-items-stretch">
              {/* Contact Information Section */}
              <Col lg={5} md={12}>
                <Card className="contact-info-card h-100">
                  <Card.Body className="p-4">
                    <h3 className="contact-section-title mb-3">Get in Touch</h3>
                    <p className="text-muted mb-4">Have questions or feedback? We'd love to hear from you!</p>
                    
                    <div className="contact-methods">
                      {/* Email */}
                      <div className="contact-item">
                        <div className="contact-item-icon">
                          <FaEnvelope />
                        </div>
                        <div className="contact-item-content">
                          <h6 className="contact-item-title">Email Us</h6>
                          <a href="mailto:support@unitradegh.com" className="contact-link">
                            support@unitradegh.com
                          </a>
                        </div>
                      </div>
                      
                      {/* Phone */}
                      <div className="contact-item">
                        <div className="contact-item-icon">
                          <FaPhone />
                        </div>
                        <div className="contact-item-content">
                          <h6 className="contact-item-title">Call Us</h6>
                          
                          <div className="contact-team mb-3">
                            <p className="team-label mb-1">Technical Team</p>
                            <div className="phone-numbers">
                              <a href="tel:+233530913920">053 091 3920</a>
                              <a href="tel:+233545048441">054 504 8441</a>
                              <a href="tel:+233559489824">055 948 9824</a>
                            </div>
                          </div>
                          
                          <div className="contact-team">
                            <p className="team-label mb-1">Customer Service</p>
                            <div className="phone-numbers">
                              <a href="tel:+233542557989">054 255 7989</a>
                              <a href="tel:+233535542438">053 554 2438</a>
                              <a href="tel:+233558059440">055 805 9440</a>
                            </div>
                          </div>
                          
                          <p className="contact-hours mt-2">
                            <small>Monday - Friday, 8am - 5pm GMT</small>
                          </p>
                        </div>
                      </div>
                      
                      {/* Location */}
                      <div className="contact-item">
                        <div className="contact-item-icon">
                          <FaMapMarkerAlt />
                        </div>
                        <div className="contact-item-content">
                          <h6 className="contact-item-title">Visit Us</h6>
                          <p className="mb-0">University of Energy and Natural Resources</p>
                          <p className="mb-0">Sunyani, Ghana</p>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              
              {/* Contact Form Section */}
              <Col lg={7} md={12}>
                <Card className="contact-form-card h-100">
                  <Card.Body className="p-4">
                    <h3 className="contact-section-title mb-4">Send us a Message</h3>
                    
                    {error && (
                      <Alert variant="danger" className="mb-3">
                        <strong>Error:</strong> {error}
                      </Alert>
                    )}
                    
                    {submitted ? (
                      <Alert variant="success" className="text-center py-4">
                        <FaPaperPlane size={40} className="mb-3 d-block mx-auto" />
                        <h5>Thank you for your message!</h5>
                        <p className="mb-0">We'll get back to you as soon as possible.</p>
                      </Alert>
                    ) : (
                      <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Group controlId="formName">
                              <Form.Label>Your Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="John Doe"
                                required
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col md={6}>
                            <Form.Group controlId="formEmail">
                              <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="john@example.com"
                                required
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col xs={12}>
                            <Form.Group controlId="formSubject">
                              <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                placeholder="What's this about?"
                                required
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col xs={12}>
                            <Form.Group controlId="formMessage">
                              <Form.Label>Your Message <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={6}
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="How can we help you?"
                                required
                              />
                            </Form.Group>
                          </Col>
                          
                          <Col xs={12}>
                            <Button 
                              variant="primary" 
                              type="submit" 
                              size="lg" 
                              className="w-100 contact-submit-btn"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                  />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <FaPaperPlane className="me-2" />
                                  Send Message
                                </>
                              )}
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

export default ContactUs;
